import React from "react"
import { Typography, Divider, Row, Col } from "antd"
import PropTypes from "prop-types"
import { useWatch } from "antd/es/form/Form"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { numberToCurrency } from "helpers/string"
import { calculatePaymentBreakdown } from "utils/bookingCalculations"
import { colors } from "themes/variables"

const { Text } = Typography

const PriceBreakdown = ({ form, booking, isEdit, isPriceDecreased, hasDepositPayment }) => {
  const { t } = useTranslation()
  const depositPercent = useWatch("depositPercent", form) || (booking?.deposit_percentage ? booking.deposit_percentage : "")
  const customDepositAmount = useWatch("customDepositAmount", form)
  const paymentOption = useWatch("paymentOption", form)
  const depositOnly = useWatch("depositOnly", form)
  const {
    experienceBookingData: { bookingSelections }
  } = useSelector((state) => state.experiences)

  // Calculate all payment values using the utility function
  const {
    percent,
    depositAmount,
    depositDueNow,
    balanceAmount,
    payment_history = [],
    refundDueNow
  } = calculatePaymentBreakdown({
    booking,
    bookingSelections,
    depositPercent,
    customDepositAmount,
    isPriceDecreased,
    paymentOption
  })

  return (
    <div>
      {bookingSelections?.breakdown?.map((item) => (
        <Row key={`${item?.label}-${item?.amount}`} justify="space-between" style={{ marginTop: 8 }}>
          <Col>
            <Text>{item?.label}</Text>
          </Col>
          <Col>
            <Text>{item?.amount}</Text>
          </Col>
        </Row>
      ))}

      <Divider style={{ margin: "16px 0" }} />

      <Row justify="space-between">
        <Col>
          <Text strong>{t("booking.labels.total")}</Text>
        </Col>
        <Col>
          <Text strong>{numberToCurrency(bookingSelections?.amount)}</Text>
        </Col>
      </Row>
      {percent > 0 && (
        <>
          {isEdit &&
            payment_history?.map((history) => {
              const { type, method, last4, amount, id } = history || {}
              const isRefund = type?.toLowerCase() === "refund"
              const key = id || `${type}-${method}-${last4}-${amount}`
              return (
                <Row key={key} justify="space-between" style={{ marginTop: 8 }}>
                  <Col>
                    <Text>
                      {type || ""} ({method || ""}
                      {last4 && ` **** ${last4}`})
                    </Text>
                  </Col>
                  <Col>
                    <Text color={isRefund ? colors.error : colors.textPrimary}>{amount ? numberToCurrency(amount) : ""}</Text>
                  </Col>
                </Row>
              )
            })}
          {((isEdit && hasDepositPayment && !isPriceDecreased) || !isEdit) && (
            <Row justify="space-between" style={{ marginTop: 8 }}>
              <Col>
                <Text>{isEdit ? t("booking.labels.deposit_due_now") : depositOnly && t("booking.labels.pay_now_deposit")}</Text>
              </Col>
              <Col>
                <Text>{numberToCurrency(isEdit ? depositDueNow : depositAmount)}</Text>
              </Col>
            </Row>
          )}
          {balanceAmount >= 0 && (
            <Row justify="space-between" style={{ marginTop: 8 }}>
              <Col>
                <Text strong>{t("booking.labels.remaining_balance")}</Text>
              </Col>
              <Col>
                <Text strong>{numberToCurrency(balanceAmount)}</Text>
              </Col>
            </Row>
          )}
        </>
      )}
      {isEdit && refundDueNow > 0 && (
        <Row justify="space-between" style={{ marginTop: 8 }}>
          <Col>
            <Text strong>{t("booking.labels.refund_due")}</Text>
          </Col>
          <Col>
            <Text strong>{numberToCurrency(refundDueNow)}</Text>
          </Col>
        </Row>
      )}
    </div>
  )
}

PriceBreakdown.propTypes = {
  form: PropTypes.object.isRequired,
  booking: PropTypes.object.isRequired,
  isEdit: PropTypes.bool.isRequired,
  isPriceDecreased: PropTypes.bool.isRequired,
  hasDepositPayment: PropTypes.bool.isRequired
}

export default PriceBreakdown
